import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Globe, Phone, Mail, MapPin, Menu, X } from 'lucide-react'
import { SiFacebook, SiX, SiLinkedin, SiInstagram } from 'react-icons/si'
import { COMPANY_INFO } from '@/lib/index'
import { useLanguage } from '@/i18n/LanguageContext'

function scrollToSection(id: string) {
  const el = document.getElementById(id)
  if (el) {
    const headerHeight = 80
    const top = el.getBoundingClientRect().top + window.scrollY - headerHeight
    window.scrollTo({ top, behavior: 'smooth' })
  }
}

export function Header() {
  const { language, setLanguage, t } = useLanguage()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { id: 'about', label: t('nav.about') },
    { id: 'services', label: t('nav.services') },
    { id: 'portfolio', label: t('nav.portfolio') },
    { id: 'process', label: t('nav.process') },
    { id: 'reviews', label: t('nav.reviews') },
    { id: 'pricing', label: t('nav.pricing') },
  ]

  const languageOptions = [
    { code: 'EN' as const, label: 'English' },
    { code: 'ZH' as const, label: '中文' },
    { code: 'FR' as const, label: 'Français' },
  ]

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-secondary/95 backdrop-blur-xl shadow-2xl shadow-secondary/20'
          : 'bg-secondary/75 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-3 group"
          >
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/30 group-hover:shadow-primary/50 transition-all duration-300 group-hover:scale-105">
              <span className="text-primary-foreground font-bold text-xl" style={{ fontFamily: 'DM Serif Display, serif' }}>V</span>
            </div>
            <div>
              <span className="text-secondary-foreground font-bold text-xl tracking-widest">VANCORE</span>
              <div className="text-primary text-[10px] tracking-[0.2em] font-medium uppercase -mt-0.5 opacity-80">Building Solutions</div>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-secondary-foreground/75 hover:text-primary transition-colors text-sm font-medium px-4 py-2 rounded-lg hover:bg-secondary-foreground/5 relative group"
              >
                {link.label}
                <span className="absolute bottom-1 left-4 right-4 h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </button>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 text-secondary-foreground/80 hover:text-secondary-foreground hover:bg-secondary-foreground/10">
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs font-medium">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-secondary border-secondary-foreground/20">
                {languageOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.code}
                    onClick={() => setLanguage(option.code)}
                    className={`text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer ${language === option.code ? 'text-primary font-semibold' : ''}`}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Phone */}
            <a
              href={`tel:${COMPANY_INFO.PHONE}`}
              className="hidden md:flex items-center gap-2 text-primary font-mono text-sm hover:text-primary/80 transition-colors"
            >
              <Phone className="w-4 h-4" />
              {COMPANY_INFO.PHONE}
            </a>

            {/* CTA Button */}
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all duration-300 hover:-translate-y-0.5"
            >
              {t('nav.cta')}
            </Button>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-secondary-foreground p-2"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-secondary/98 backdrop-blur-xl border-t border-secondary-foreground/10"
          >
            <div className="px-6 py-4 space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => { scrollToSection(link.id); setMobileOpen(false) }}
                  className="block w-full text-left text-secondary-foreground/80 hover:text-primary py-3 px-4 rounded-lg hover:bg-secondary-foreground/5 transition-colors text-sm font-medium"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-4 pb-2">
                <Button
                  onClick={() => { scrollToSection('contact'); setMobileOpen(false) }}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {t('nav.cta')}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

export function Footer() {
  const { t } = useLanguage()

  const quickLinks = [
    { id: 'about', label: t('nav.about') },
    { id: 'services', label: t('nav.services') },
    { id: 'portfolio', label: t('nav.portfolio') },
    { id: 'pricing', label: t('nav.pricing') },
  ]

  const serviceLinks = [
    t('footer.services.flooring'),
    t('footer.services.cabinets'),
    t('footer.services.interior'),
    t('footer.services.renovation'),
  ]

  const socialLinks = [
    { icon: SiFacebook, href: '#', label: 'Facebook' },
    { icon: SiX, href: '#', label: 'X' },
    { icon: SiLinkedin, href: '#', label: 'LinkedIn' },
    { icon: SiInstagram, href: '#', label: 'Instagram' },
  ]

  return (
    <footer className="bg-secondary text-secondary-foreground">
      {/* Gold accent line */}
      <div className="h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
                <span className="text-primary-foreground font-bold text-xl" style={{ fontFamily: 'DM Serif Display, serif' }}>V</span>
              </div>
              <div>
                <span className="text-secondary-foreground font-bold text-xl tracking-widest block">VANCORE</span>
                <div className="text-primary text-[10px] tracking-[0.2em] font-medium uppercase opacity-80">Building Solutions</div>
              </div>
            </div>
            <p className="text-secondary-foreground/60 text-sm mb-6 leading-relaxed">
              {t('footer.company.description')}
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => {
                const Icon = social.icon
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-secondary-foreground/10 hover:bg-primary flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-primary/30"
                    aria-label={social.label}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                )
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-base mb-5 text-primary tracking-wide uppercase text-xs">{t('footer.quickLinks.title')}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-secondary-foreground/60 hover:text-primary transition-colors text-sm flex items-center gap-2 group"
                  >
                    <span className="w-4 h-px bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-base mb-5 text-primary tracking-wide uppercase text-xs">{t('footer.services.title')}</h3>
            <ul className="space-y-3">
              {serviceLinks.map((label, index) => (
                <li key={index}>
                  <span className="text-secondary-foreground/60 text-sm flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-base mb-5 text-primary tracking-wide uppercase text-xs">{t('footer.contact.title')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <a href={`tel:${COMPANY_INFO.PHONE}`} className="text-secondary-foreground/60 hover:text-primary transition-colors text-sm">
                  {COMPANY_INFO.PHONE}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <a href={`mailto:${COMPANY_INFO.EMAIL}`} className="text-secondary-foreground/60 hover:text-primary transition-colors text-sm">
                  {COMPANY_INFO.EMAIL}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-secondary-foreground/60 text-sm leading-relaxed">
                  {COMPANY_INFO.ADDRESS}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-secondary-foreground/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-secondary-foreground/40 text-sm">
              {t('footer.copyright')}
            </p>
            <p className="text-secondary-foreground/40 text-sm">
              {COMPANY_INFO.SERVICE_AREAS.join(' · ')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
